// =============================================================================
// domain/propTilt.ts — the Tilt Signal banner at the top of the Prop Report.
// -----------------------------------------------------------------------------
// Five behavioural signals over the trades on ACTIVE prop accounts. Two or more
// firing draws a banner; four or more turns it red. Ported from beta's
// `computeTiltSignals`.
//
// ── ⚠ THREE DIFFERENT THINGS IN THIS CLIENT ARE CALLED "TILT" ──
//
//   `disciplineSignals.tiltSignal`  Analytics — does your win rate FALL after a
//                                   loss? A gap between two win rates.
//   `journal.tiltCheck`             Journal/Session — four flags over ONE day.
//   this file                       Prop Report — a five-signal composite over
//                                   the recent window, on prop accounts only.
//
// They answer different questions and none is derivable from another. The name
// collision is beta's — two of its cards are literally headed "Tilt Signal" — and
// it is worth knowing before someone "deduplicates" them.
//
// ── 🚨 EVERY SIGNAL HERE IS CLASS L, AND EVERY ONE FAILS SILENT (D47) ──
//
// Three of the five compare a recent window against an ALL-TIME baseline. Under a
// bounded read the baseline is computed from recent trades too, so it moves
// toward the window it is being compared against and the ratio collapses:
//
//   • sizing creep   recent ÷ baseline → ~1, never clears 1.5×
//   • frequency      typical rate rises to meet the spike, never clears 2×
//   • grade decay    "overall" average becomes the recent average
//   • loss streak    truncated at the read boundary, so 19 can read as 4
//
// Not one of them reads as uncertain. Every one of them reads as CALM. A warning
// system that fails silent is worse than no warning system, which is why this
// module is reached through `selectors/propReport.ts` and its `WholeBook` gate
// rather than off a range-scoped read.
//
// ── ⚠ TIME IS WALL-CLOCK HERE, NOT SESSION DATE ──
//
// The windows are 24 hours and 4 hours. `effectiveTradeDate` would move a 20:00
// trade onto the next session day and put it four hours in the wrong direction,
// so this reads the RAW `date` + `timeIn`, as beta does. Every other Class-L
// number on this tab follows the day-boundary mode; this one cannot.
// =============================================================================

/** What the composite needs from a trade. */
export interface TiltTrade {
  accountId: string | null;
  /** Raw calendar date — see the header on why not `effectiveTradeDate`. */
  date: string | null;
  timeIn: string | null;
  netPnl: number | null;
  qty: number | null;
  grade: string | null;
  booleans: Record<string, boolean> | null;
}

export type TiltLevel = 'calm' | 'elevated' | 'risk';

export interface TiltReason {
  /** Stable id so a test can name a signal without matching prose. */
  key: 'streak' | 'sizing' | 'frequency' | 'tagged' | 'grade';
  label: string;
  detail: string;
}

export interface TiltComposite {
  /** False when there are no trades on an active prop account at all. */
  available: boolean;
  /** How many of the five fired, 0–5. */
  score: number;
  level: TiltLevel;
  advice: string;
  reasons: TiltReason[];
  /**
   * ⚠ THE TIMESTAMP EVERY WINDOW IS MEASURED BACK FROM — the trader's LAST
   * TRADE, not now. Beta does the same and never says so on screen, so a banner
   * computed off a trade from last Tuesday reads as a live warning today. See
   * PD-29: the banner states this.
   */
  asOf: { date: string; time: string | null } | null;
}

/** Minimum trades before the frequency signal is allowed an opinion — beta's. */
const FREQ_MIN_TRADES = 30;
/** Minimum graded trades overall, and within the last five. Beta's. */
const GRADE_MIN_TOTAL = 10;
const GRADE_MIN_RECENT = 3;
/** How far a recent grade average must fall below the overall one. Beta's. */
const GRADE_DROP = 0.5;
/** Consecutive losers before the streak signal fires. Beta's. */
const STREAK_MIN = 3;
/** Recent average contracts must exceed the baseline by this multiple. Beta's. */
const SIZING_MULTIPLE = 1.5;
/** …over at least this many recent trades, so one big trade cannot fire it. */
const SIZING_MIN_RECENT = 3;
/** Recent 4h count must exceed the typical 4h count by this multiple. Beta's. */
const FREQ_MULTIPLE = 2;
const FREQ_MIN_RECENT = 4;

const HOUR_MS = 3_600_000;

const GRADE_POINTS: Record<string, number> = { A: 4, B: 3, C: 2, D: 1, F: 0 };

/**
 * `2026-08-05` + `11:13:06` → epoch ms, via `Date.UTC`.
 *
 * ⚠ UTC DELIBERATELY, even though these are local times. Every use below is a
 * DIFFERENCE between two of them, so the zone cancels — except across a DST
 * boundary, where a local-time parse would silently make one window an hour
 * longer than the next. `Date.UTC` has no DST, which makes the arithmetic the
 * same in March as in July.
 *
 * ⚠ It is also why this module has no wall clock in it (D26): it converts the
 * strings it is handed and never asks what time it is.
 */
export function tradeStamp(date: string | null, timeIn: string | null): number | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(date ?? ''));
  if (!d) return null;
  // Beta's fallback: a trade with no entry time is treated as midday, so it
  // lands inside its own day rather than at its edge.
  const t = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(String(timeIn ?? '')) ?? null;
  return Date.UTC(
    Number(d[1]), Number(d[2]) - 1, Number(d[3]),
    t ? Number(t[1]) : 12, t ? Number(t[2]) : 0, t && t[3] ? Number(t[3]) : 0,
  );
}

/** Trades on the given accounts, oldest first. Undated trades are dropped. */
function orderedTrades(
  accountIds: ReadonlySet<string>,
  trades: readonly TiltTrade[],
): { t: TiltTrade; ts: number }[] {
  const out: { t: TiltTrade; ts: number }[] = [];
  for (const t of trades) {
    if (!t || t.accountId == null || !accountIds.has(t.accountId)) continue;
    const ts = tradeStamp(t.date, t.timeIn);
    if (ts == null) continue;
    out.push({ t, ts });
  }
  return out.sort((a, b) => a.ts - b.ts);
}

const mean = (xs: readonly number[]): number =>
  xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : 0;

const contracts = (rows: readonly { t: TiltTrade }[]): number[] =>
  rows.map((r) => Number(r.t.qty) || 0).filter((q) => q > 0);

/**
 * Hours the trader was ACTUALLY at the screen — each day's first trade to its
 * last, summed, with a one-hour floor per active day.
 *
 * 🚨 THIS IS PD-27, AND IT IS THE DIFFERENCE BETWEEN A SIGNAL AND A NUISANCE.
 * Beta divides the trade count by the hours between the FIRST and LAST trade in
 * the whole history — which counts every night, weekend and fortnight away from
 * the desk as time spent not trading. On Josh's 820 trades that denominator is
 * 3,121 hours, giving a "typical" rate of 1.05 trades per four hours when his
 * median trading DAY is 8 trades. Anything above 2.1 then reads as a spike, so
 * the signal fired on 51% of his trading days — a spike detector that mostly
 * detects that he traded.
 *
 * Measured over the 409 hours he was actually trading, the typical rate is 8.0
 * per four hours and 9% of his days trip it. Beta's own explainer says the test
 * is against "the trader's typical rate"; calendar hours are not that.
 *
 * ⚠ The one-hour floor exists because a day with a single trade spans zero hours
 * and would otherwise add trades to the numerator and nothing to the
 * denominator.
 */
export function activeTradingHours(rows: readonly { t: TiltTrade; ts: number }[]): number {
  const byDay = new Map<string, { min: number; max: number }>();
  for (const r of rows) {
    const d = String(r.t.date);
    const cur = byDay.get(d);
    if (!cur) byDay.set(d, { min: r.ts, max: r.ts });
    else { if (r.ts < cur.min) cur.min = r.ts; if (r.ts > cur.max) cur.max = r.ts; }
  }
  let hours = 0;
  for (const { min, max } of byDay.values()) hours += Math.max(1, (max - min) / HOUR_MS);
  return hours;
}

/**
 * The five signals, and the banner they justify.
 *
 * @param accountIds Accounts the report counts as live — funded + eval, with
 *   `excludeFromMetrics` already dropped. Passed in rather than derived so this
 *   module never has to know what a prop account is.
 */
export function propTiltSignals(
  accountIds: ReadonlySet<string>,
  trades: readonly TiltTrade[],
): TiltComposite {
  const rows = orderedTrades(accountIds, trades);
  if (!rows.length) {
    return { available: false, score: 0, level: 'calm', advice: '', reasons: [], asOf: null };
  }

  const last = rows[rows.length - 1]!;
  const lastTs = last.ts;
  const within = (hours: number) => rows.filter((r) => r.ts >= lastTs - hours * HOUR_MS);
  const last24 = within(24);
  const last4 = within(4);

  const reasons: TiltReason[] = [];

  // ── 1. LOSS STREAK ────────────────────────────────────────────────────────
  // ⚠ A trade with NO net P&L breaks the streak, matching beta's `|| 0`. It
  // cannot be called a loser, and an unpriced trade is not evidence of calm
  // either — but calling it a loss would let a broken import manufacture tilt.
  const streakRows: typeof rows = [];
  for (let i = rows.length - 1; i >= 0; i--) {
    const row = rows[i]!;
    if ((row.t.netPnl ?? 0) < 0) streakRows.unshift(row); else break;
  }
  if (streakRows.length >= STREAK_MIN) {
    // ⚠ PD-29: the SPAN, which beta does not print. Nineteen losers inside half
    // an hour is tilt; nineteen across three weeks is a strategy that stopped
    // working. The same number means opposite things and the banner is where
    // the trader decides which one they are looking at.
    const span = (streakRows[streakRows.length - 1]!.ts - streakRows[0]!.ts) / HOUR_MS;
    reasons.push({
      key: 'streak',
      label: 'Loss streak',
      detail: `${streakRows.length} consecutive losers ${describeSpan(span)}`,
    });
  }

  // ── 2. SIZING CREEP ───────────────────────────────────────────────────────
  // 🚨 PD-28: THE BASELINE EXCLUDES THE WINDOW IT IS COMPARED AGAINST.
  // Beta averages contracts over ALL trades including the last 24 hours, so the
  // baseline is pulled toward the very window under test — and the more of the
  // book sits inside that window, the less the signal can fire. On the account
  // that produced Josh's screenshot, 89% of its trades were inside the 24-hour
  // window: baseline 4.26 contaminated against 4.70 clean. The contamination is
  // always in the direction of NOT warning, and it is worst on a fresh account
  // being traded hard, which is the exact case the signal exists for.
  const recentIds = new Set(last24.map((r) => r.t));
  const baselineRows = rows.filter((r) => !recentIds.has(r.t));
  const baseQty = mean(contracts(baselineRows));
  const recentQty = contracts(last24);
  const recentAvg = mean(recentQty);
  // ⚠ `baseQty > 0` also covers "the whole book is inside the window" — with
  // nothing outside it there is no baseline, and comparing the window with
  // itself would report a ratio of exactly 1 and look like a considered "calm".
  if (baseQty > 0 && recentQty.length >= SIZING_MIN_RECENT && recentAvg > baseQty * SIZING_MULTIPLE) {
    const pct = ((recentAvg / baseQty) - 1) * 100;
    reasons.push({
      key: 'sizing',
      label: 'Sizing creep',
      detail: `Avg ${recentAvg.toFixed(1)} contracts in the last 24h vs ${baseQty.toFixed(1)} before that (+${pct.toFixed(0)}%)`,
    });
  }

  // ── 3. FREQUENCY SPIKE ────────────────────────────────────────────────────
  // See `activeTradingHours` above for PD-27 — the whole finding is the
  // denominator.
  if (rows.length >= FREQ_MIN_TRADES) {
    const typical4h = (rows.length / activeTradingHours(rows)) * 4;
    if (last4.length >= FREQ_MIN_RECENT && last4.length > typical4h * FREQ_MULTIPLE) {
      reasons.push({
        key: 'frequency',
        label: 'Frequency spike',
        detail: `${last4.length} trades in the last 4h vs ~${typical4h.toFixed(1)} in a typical 4h of trading`,
      });
    }
  }

  // ── 4. TAGGED MISTAKES ────────────────────────────────────────────────────
  // The trader's OWN judgment, and the highest-confidence input here: the other
  // four infer, this one is told.
  const tagged = last24.filter(
    (r) => r.t.booleans?.fomoTrade === true || r.t.booleans?.revengeTrade === true,
  );
  if (tagged.length) {
    const kinds = new Set<string>();
    for (const r of tagged) {
      if (r.t.booleans?.fomoTrade === true) kinds.add('FOMO');
      if (r.t.booleans?.revengeTrade === true) kinds.add('revenge');
    }
    reasons.push({
      key: 'tagged',
      label: 'Tagged mistakes',
      detail: `${tagged.length} trade${tagged.length === 1 ? '' : 's'} you flagged (${[...kinds].join(', ')}) in the last 24h`,
    });
  }

  // ── 5. GRADE DECAY ────────────────────────────────────────────────────────
  const gradeOf = (t: TiltTrade): number | null => GRADE_POINTS[String(t.grade ?? '')] ?? null;
  const allGrades = rows.map((r) => gradeOf(r.t)).filter((g): g is number => g != null);
  const lastFive = rows.slice(-5).map((r) => gradeOf(r.t)).filter((g): g is number => g != null);
  if (allGrades.length >= GRADE_MIN_TOTAL && lastFive.length >= GRADE_MIN_RECENT) {
    const overall = mean(allGrades);
    const recent = mean(lastFive);
    if (recent < overall - GRADE_DROP) {
      reasons.push({
        key: 'grade',
        label: 'Grade decay',
        detail: `You graded your last ${lastFive.length} at ${recent.toFixed(1)} against ${overall.toFixed(1)} overall`,
      });
    }
  }

  const score = reasons.length;
  const level: TiltLevel = score >= 4 ? 'risk' : score >= 2 ? 'elevated' : 'calm';

  return {
    available: true,
    score,
    level,
    advice: level === 'risk'
      ? 'Step away or size down — multiple signals firing.'
      : level === 'elevated'
        ? 'Watch sizing and re-entry pace.'
        : '',
    reasons,
    asOf: { date: String(last.t.date), time: last.t.timeIn ?? null },
  };
}

/**
 * "in 24 minutes" / "over 3 days".
 *
 * ⚠ Rounds to the unit it names rather than printing "0.4 hours". A span under a
 * minute reads "back to back", which is the honest description of a run of
 * scratched entries and the one shape a duration in minutes would hide.
 */
export function describeSpan(hours: number): string {
  const mins = hours * 60;
  if (mins < 1) return 'back to back';
  if (mins < 90) return `in ${Math.round(mins)} minute${Math.round(mins) === 1 ? '' : 's'}`;
  if (hours < 36) return `over ${Math.round(hours)} hours`;
  return `over ${Math.round(hours / 24)} days`;
}
